/**
 * Garden Layout Image Module
 * Handles garden image capture, saving, and layout management
 */

import { trackDownload } from './analytics.js';
import { loadExportLibraries } from './load-export-libs.js';
import { subscriptionManager } from './subscription.js';
import { saveCurrentBedToJournal } from './save-to-journal.js';

function showGardenMessageModal(title, message, options = {}) {
    const {
        confirmText = 'OK',
        cancelText = null,
        onConfirm = null
    } = options;

    // Lazy-create the modal once, then reuse it for all subsequent calls.
    let modal = document.getElementById('garden-message-modal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'garden-message-modal';
        modal.className = 'modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');

        const modalContent = document.createElement('div');
        modalContent.className = 'modal-content';

        const h3 = document.createElement('h3');
        h3.textContent = title;

        const p = document.createElement('p');
        p.id = 'garden-message-modal-text';
        p.textContent = message;

        const buttons = document.createElement('div');
        buttons.className = 'modal-buttons';

        const confirmBtn = document.createElement('button');
        confirmBtn.id = 'garden-message-modal-confirm';
        confirmBtn.className = 'modal-btn confirm-btn';
        confirmBtn.type = 'button';
        confirmBtn.textContent = confirmText;

        const cancelBtn = document.createElement('button');
        cancelBtn.id = 'garden-message-modal-cancel';
        cancelBtn.className = 'modal-btn cancel-btn';
        cancelBtn.type = 'button';
        cancelBtn.textContent = cancelText || '';

        buttons.appendChild(confirmBtn);
        buttons.appendChild(cancelBtn);
        modalContent.appendChild(h3);
        modalContent.appendChild(p);
        modalContent.appendChild(buttons);
        modal.appendChild(modalContent);

        document.body.appendChild(modal);

        confirmBtn.addEventListener('click', () => {
            // onConfirm handler is attached/updated below on every call.
            modal.style.display = 'none';
        });

        cancelBtn.addEventListener('click', () => {
            modal.style.display = 'none';
        });

        // Close when clicking the dimmed backdrop.
        modal.addEventListener('click', (e) => {
            if (e.target === modal) modal.style.display = 'none';
        });
    } else {
        const h3 = modal.querySelector('h3');
        if (h3) h3.textContent = title;
        const p = modal.querySelector('#garden-message-modal-text');
        if (p) p.textContent = message;
    }

    // Update button labels & visibility on each call.
    const confirmBtn = modal.querySelector('#garden-message-modal-confirm');
    if (confirmBtn) confirmBtn.textContent = confirmText;
    const cancelBtn = modal.querySelector('#garden-message-modal-cancel');
    if (cancelBtn) {
        cancelBtn.textContent = cancelText || '';
        cancelBtn.style.display = cancelText ? 'inline-flex' : 'none';
    }

    // Update title/message each time (creation path already did; update path did via selectors).
    const h3 = modal.querySelector('h3');
    if (h3) h3.textContent = title;
    const p = modal.querySelector('#garden-message-modal-text');
    if (p) p.textContent = message;

    // Ensure latest callback is used for this modal invocation.
    // (The click handler is created only once, so we must update it on every call.)
    if (confirmBtn) {
        confirmBtn.onclick = () => {
            modal.style.display = 'none';
            if (typeof onConfirm === 'function') onConfirm();
        };
    }
    if (cancelBtn) {
        cancelBtn.onclick = () => {
            modal.style.display = 'none';
        };
    }

    modal.style.display = 'flex';
}

// Function to capture the garden layout as an image (loads html2canvas on first use)
async function captureGardenLayoutAsImage() {
    await loadExportLibraries();
    if (typeof window.html2canvas === 'undefined') {
        showGardenMessageModal('Error', 'html2canvas library is required but not loaded.');
        return;
    }

    // Require a bed name before sending to layout
    const gardenNameInput = document.getElementById('garden-name');
    const gardenName = gardenNameInput ? gardenNameInput.value.trim() : '';
    if (!gardenName) {
        showGardenMessageModal('Missing name', 'Please name your garden bed before sending to layout.');
        gardenNameInput?.focus();
        return;
    }
    
    // Show loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'image-capture-loading';
    loadingIndicator.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Capturing garden layout...';
    document.body.appendChild(loadingIndicator);
    
    // Get the grid element
    const gridElement = document.getElementById('grid');
    
    if (!gridElement) {
        showGardenMessageModal('Error', 'Could not find the garden grid element.');
        document.body.removeChild(loadingIndicator);
        return;
    }
    
    // Use html2canvas to capture the grid as an image
    window.html2canvas(gridElement, {
        backgroundColor: null, // remove the background color
        scale: 2, // Higher resolution
        logging: false, // Disable logging
        allowTaint: true,
        useCORS: true
    }).then(canvas => {
        // Convert canvas to PNG image
        const imageData = canvas.toDataURL('image/png');
        
        // Create layout data object
        const layoutData = {
            id: Date.now().toString(),
            name: gardenName,
            imageData: imageData,
            createdAt: new Date().toISOString(),
            dimensions: {
                width: canvas.width,
                height: canvas.height
            },
            rotation: 0 // Add rotation property to track current rotation
        };
        
        // Save the image layout
        saveImageLayout(layoutData);
        
        // Add to garden beds container
        addImageLayoutToGardenBedsContainer(layoutData);
        
        // Remove loading indicator
        document.body.removeChild(loadingIndicator);
        
        const { isLoggedIn = false, isPremium = false } = subscriptionManager?.getSubscriptionInfo?.() ?? {};
        const shouldSaveToJournal = isLoggedIn && isPremium;

        // Show confirmation (only claim journal save for premium users).
        const journalSuffix = shouldSaveToJournal ? ' and also saved to your journal.' : '.';
        showGardenMessageModal(
            'Garden Bed Saved',
            `The Garden Bed: "${gardenName}" has been sent to the Garden Layout!${journalSuffix}`
        );

        // Send the same garden bed to the journal (same as "Save to Journal" button)
        if (shouldSaveToJournal) {
            saveCurrentBedToJournal();
        }
    }).catch(error => {
        console.error('Error capturing garden layout:', error);
        // Show confirmation instead of error
        showGardenMessageModal(
            'Garden Bed Saved',
            `The Garden Bed: "${gardenName}" has been sent to the Garden Layout!`
        );
        document.body.removeChild(loadingIndicator);
    });
}

// Function to save image layout to localStorage
function saveImageLayout(layoutData) {
    // Get existing saved layouts or initialize empty array
    let savedImageLayouts = [];
    const storedImageLayouts = localStorage.getItem('garden_image_layouts');
    
    if (storedImageLayouts) {
        try {
            savedImageLayouts = JSON.parse(storedImageLayouts);
        } catch (error) {
            console.error('Error parsing saved layouts:', error);
            savedImageLayouts = [];
        }
    }
    
    // Add new layout to saved layouts
    savedImageLayouts.push(layoutData);
    
    // Save to localStorage
    try {
        localStorage.setItem('garden_image_layouts', JSON.stringify(savedImageLayouts));
    } catch (error) {
        console.error('Error saving layout to localStorage:', error);
    }
}

// Function to load saved image layouts
function loadSavedImageLayouts() {
    const storedImageLayouts = localStorage.getItem('garden_image_layouts');
    
    if (storedImageLayouts) {
        try {
            const savedImageLayouts = JSON.parse(storedImageLayouts);
            
            // Add each saved image layout to the garden beds container
            savedImageLayouts.forEach(layout => {
                addImageLayoutToGardenBedsContainer(layout);
            });
            
            // Hide placeholder if we have layouts
            if (savedImageLayouts.length > 0) {
                const placeholder = document.querySelector('.garden-beds-placeholder');
                if (placeholder) {
                    placeholder.style.display = 'none';
                }
            }
        } catch (error) {
            console.error('Error loading saved layouts:', error);
        }
    }
}

// Function to add image layout to garden beds container
function addImageLayoutToGardenBedsContainer(layoutData) {
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    
    if (!gardenBedsContainer) {
        console.warn('Garden beds container not found');
        return;
    }
    
    // Create layout container
    const layoutContainer = document.createElement('div');
    layoutContainer.className = 'garden-image-layout';
    layoutContainer.dataset.layoutId = layoutData.id;
    
    // Set initial position if it exists in layout data, otherwise use default offset
    if (layoutData.position) {
        layoutContainer.style.position = 'absolute';
        layoutContainer.style.left = `${layoutData.position.left}px`;
        layoutContainer.style.top = `${layoutData.position.top}px`;
    } else {
        // Set default position with vertical offset to avoid Garden items container
        layoutContainer.style.position = 'absolute';
        layoutContainer.style.left = '20px';
        layoutContainer.style.top = '100px'; // Add vertical offset to position below Garden items
    }
    
    // Add CSS styles for hover behavior
    const style = document.createElement('style');
    style.textContent = `
        .garden-image-layout .element-controls,
        .garden-image-layout .resize-element-handle {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
        }
        .garden-image-layout:hover .element-controls,
        .garden-image-layout:hover .resize-element-handle {
            opacity: 1;
        }
        .garden-image-layout .duplicate-element-btn {
            background-color: #2196F3;
        }
    `;
    document.head.appendChild(style);
    
    // Create image element
    const imageElement = document.createElement('img');
    imageElement.src = layoutData.imageData;
    imageElement.alt = layoutData.name;
    imageElement.className = 'garden-layout-image';
    
    // Set initial rotation if it exists in layout data
    if (layoutData.rotation) {
        layoutContainer.style.transform = `rotate(${layoutData.rotation}deg)`;
        layoutContainer.setAttribute('data-rotation', layoutData.rotation.toString());
    }
    
    // Set initial size - scale down the original size for the container
    const maxSize = 300; // Maximum size in the container
    let width = layoutData.dimensions.width;
    let height = layoutData.dimensions.height;
    
    // Scale down proportionally if needed
    if (width > maxSize || height > maxSize) {
        const scaleFactor = Math.min(maxSize / width, maxSize / height);
        width = width * scaleFactor;
        height = height * scaleFactor;
    }
    
    layoutContainer.style.width = `${width}px`;
    layoutContainer.style.height = `${height}px`;
    
    // Add the image name
    const nameLabel = document.createElement('div');
    nameLabel.className = 'layout-name';
    nameLabel.textContent = layoutData.name;
    
    // Add control buttons container
    const controlsContainer = document.createElement('div');
    controlsContainer.className = 'element-controls';
    
    // Add delete button
    const deleteButton = document.createElement('button');
    deleteButton.className = 'element-control-btn delete-element-btn';
    deleteButton.innerHTML = '×';
    deleteButton.title = 'Delete layout';
    deleteButton.addEventListener('click', (e) => {
        e.stopPropagation();
        layoutContainer.remove();
        removeImageLayoutFromStorage(layoutData.id);
        checkAndShowPlaceholder();
    });
    
    // Add rotate button
    const rotateButton = document.createElement('button');
    rotateButton.className = 'element-control-btn rotate-element-btn';
    rotateButton.innerHTML = '↻';
    rotateButton.title = 'Rotate layout';
    rotateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        rotateLayout(layoutContainer);
    });
    
    // Add duplicate button
    const duplicateButton = document.createElement('button');
    duplicateButton.className = 'element-control-btn duplicate-element-btn';
    duplicateButton.innerHTML = '⧉';
    duplicateButton.title = 'Duplicate layout';
    duplicateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        duplicateLayout(layoutContainer);
    });
    
    // Add buttons to controls container
    controlsContainer.appendChild(deleteButton);
    controlsContainer.appendChild(rotateButton);
    controlsContainer.appendChild(duplicateButton);
    
    // Add resize handle
    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'resize-element-handle';
    resizeHandle.innerHTML = '⤡';
    resizeHandle.addEventListener('mousedown', startResize);
    resizeHandle.addEventListener('touchstart', startResize, { passive: false });
    
    // Add all elements to the container
    layoutContainer.appendChild(imageElement);
    layoutContainer.appendChild(nameLabel);
    layoutContainer.appendChild(controlsContainer);
    layoutContainer.appendChild(resizeHandle);
    
    // Make the layout draggable
    makeLayoutDraggable(layoutContainer);
    
    // Add to garden beds container
    gardenBedsContainer.appendChild(layoutContainer);
    
    // Hide placeholder
    const placeholder = document.querySelector('.garden-beds-placeholder');
    if (placeholder) {
        placeholder.style.display = 'none';
    }
}

// Rotate a layout
function rotateLayout(element) {
    // Get current rotation or default to 0
    let currentRotation = parseInt(element.getAttribute('data-rotation') || '0');
    
    // Increment rotation by 45 degrees
    currentRotation = (currentRotation + 45) % 360;
    
    // Apply rotation
    element.style.transform = `rotate(${currentRotation}deg)`;
    element.setAttribute('data-rotation', currentRotation.toString());
    
    // Update layout data
    const layoutId = element.getAttribute('data-layout-id');
    updateLayoutRotation(layoutId, currentRotation);
}

// Update layout rotation in storage
function updateLayoutRotation(layoutId, rotation) {
    const storedLayouts = localStorage.getItem('garden_image_layouts');
    if (storedLayouts) {
        try {
            const layouts = JSON.parse(storedLayouts);
            const layoutIndex = layouts.findIndex(layout => layout.id === layoutId);
            if (layoutIndex !== -1) {
                layouts[layoutIndex].rotation = rotation;
                localStorage.setItem('garden_image_layouts', JSON.stringify(layouts));
            }
        } catch (error) {
            console.error('Error updating layout rotation:', error);
        }
    }
}

// Make layout draggable
function makeLayoutDraggable(element) {
    let offsetX, offsetY, isDragging = false;
    
    element.addEventListener('mousedown', startDrag);
    element.addEventListener('touchstart', startDrag, { passive: false });
    
    function startDrag(e) {
        // Ignore if clicked on control buttons or resize handle
        if (e.target.closest('.element-control-btn, .resize-element-handle')) {
            return;
        }
        
        e.preventDefault();
        
        // Calculate initial offset
        const rect = element.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        offsetX = clientX - rect.left;
        offsetY = clientY - rect.top;
        
        isDragging = true;
        
        // Add global mouse/touch events
        document.addEventListener('mousemove', dragMove);
        document.addEventListener('touchmove', dragMove, { passive: false });
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchend', endDrag);
        
        // Add active dragging class
        element.classList.add('dragging');
    }
    
    function dragMove(e) {
        if (!isDragging) return;
        
        e.preventDefault();
        
        const container = document.getElementById('garden-beds-container');
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        // Calculate new position within container bounds
        let left = clientX - containerRect.left - offsetX;
        let top = clientY - containerRect.top - offsetY;
        
        // Apply boundary limits
        const maxLeft = containerRect.width - element.offsetWidth;
        const maxTop = containerRect.height - element.offsetHeight;
        
        left = Math.max(0, Math.min(left, maxLeft));
        top = Math.max(0, Math.min(top, maxTop));
        
        // Set new position
        element.style.position = 'absolute';
        element.style.left = `${left}px`;
        element.style.top = `${top}px`;
        
        // Update layout position in storage
        const layoutId = element.getAttribute('data-layout-id');
        updateLayoutPosition(layoutId, left, top);
    }
    
    function endDrag() {
        if (!isDragging) return;
        
        isDragging = false;
        document.removeEventListener('mousemove', dragMove);
        document.removeEventListener('touchmove', dragMove);
        document.removeEventListener('mouseup', endDrag);
        document.removeEventListener('touchend', endDrag);
        element.classList.remove('dragging');
    }
}

// Update layout position in storage
function updateLayoutPosition(layoutId, left, top) {
    const storedLayouts = localStorage.getItem('garden_image_layouts');
    if (storedLayouts) {
        try {
            const layouts = JSON.parse(storedLayouts);
            const layoutIndex = layouts.findIndex(layout => layout.id === layoutId);
            if (layoutIndex !== -1) {
                layouts[layoutIndex].position = { left, top };
                localStorage.setItem('garden_image_layouts', JSON.stringify(layouts));
            }
        } catch (error) {
            console.error('Error updating layout position:', error);
        }
    }
}

// Start resizing a layout
function startResize(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const element = this.parentElement;
    const container = document.getElementById('garden-beds-container');
    
    if (!element || !container) return;
    
    const initialWidth = element.offsetWidth;
    const initialHeight = element.offsetHeight;
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    const startY = e.clientY || (e.touches && e.touches[0].clientY);
    
    let isResizing = true;
    element.classList.add('resizing');
    
    function resizeMove(e) {
        if (!isResizing) return;
        
        e.preventDefault();
        
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;
        
        // Calculate new size
        let newWidth = initialWidth + deltaX;
        let newHeight = initialHeight + deltaY;
        
        // Enforce minimum size
        newWidth = Math.max(40, newWidth);
        newHeight = Math.max(40, newHeight);
        
        // Apply new size
        element.style.width = `${newWidth}px`;
        element.style.height = `${newHeight}px`;
        
        // Update layout size in storage
        const layoutId = element.getAttribute('data-layout-id');
        updateLayoutSize(layoutId, newWidth, newHeight);
    }
    
    function endResize() {
        if (!isResizing) return;
        
        isResizing = false;
        element.classList.remove('resizing');
        
        document.removeEventListener('mousemove', resizeMove);
        document.removeEventListener('touchmove', resizeMove);
        document.removeEventListener('mouseup', endResize);
        document.removeEventListener('touchend', endResize);
    }
    
    document.addEventListener('mousemove', resizeMove);
    document.addEventListener('touchmove', resizeMove, { passive: false });
    document.addEventListener('mouseup', endResize);
    document.addEventListener('touchend', endResize);
}

// Update layout size in storage
function updateLayoutSize(layoutId, width, height) {
    const storedLayouts = localStorage.getItem('garden_image_layouts');
    if (storedLayouts) {
        try {
            const layouts = JSON.parse(storedLayouts);
            const layoutIndex = layouts.findIndex(layout => layout.id === layoutId);
            if (layoutIndex !== -1) {
                layouts[layoutIndex].dimensions = { width, height };
                localStorage.setItem('garden_image_layouts', JSON.stringify(layouts));
            }
        } catch (error) {
            console.error('Error updating layout size:', error);
        }
    }
}

// Download all garden layouts as a single image
async function downloadEntireGardenPlan() {
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    
    if (!gardenBedsContainer) {
        alert('Error: Could not find the garden beds container.');
        return;
    }
    
    // Show loading indicator (libs may load now)
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'image-capture-loading';
    loadingIndicator.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Generating garden plan...';
    document.body.appendChild(loadingIndicator);
    
    try {
        await loadExportLibraries();
    } catch (e) {
        document.body.removeChild(loadingIndicator);
        alert('Error: Could not load export library. Please try again.');
        return;
    }
    if (typeof window.html2canvas === 'undefined') {
        document.body.removeChild(loadingIndicator);
        alert('Error: html2canvas library is required but not loaded.');
        return;
    }
    
    const gardenNameInput = document.getElementById('garden-name');
    const gardenName = gardenNameInput ? 
                       gardenNameInput.value.trim() || 'My Garden Plan' :
                       'My Garden Plan';
    
    window.html2canvas(gardenBedsContainer, {
        backgroundColor: '#d5f4b9', // Match container background
        scale: 2, // Higher resolution
        logging: false,
        allowTaint: true,
        useCORS: true
    }).then(canvas => {
        // Create download link and open in new tab
        const imageDataUrl = canvas.toDataURL('image/png');
        const filename = `${gardenName} - Complete Plan.png`;
        
        // Open in new tab
        window.open(imageDataUrl, '_blank');
        
        // Also trigger download
        const downloadLink = document.createElement('a');
        downloadLink.download = filename;
        downloadLink.href = imageDataUrl;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        trackDownload('image', filename);
        
        // Remove loading indicator
        document.body.removeChild(loadingIndicator);
    }).catch(error => {
        console.error('Error generating garden plan:', error);
        alert('Error generating garden plan. Please try again.');
        document.body.removeChild(loadingIndicator);
    });
}

// Remove image layout from storage
function removeImageLayoutFromStorage(layoutId) {
    const storedImageLayouts = localStorage.getItem('garden_image_layouts');
    
    if (storedImageLayouts) {
        try {
            let savedImageLayouts = JSON.parse(storedImageLayouts);
            
            // Filter out the deleted layout
            savedImageLayouts = savedImageLayouts.filter(layout => layout.id !== layoutId);
            
            // Update localStorage
            localStorage.setItem('garden_image_layouts', JSON.stringify(savedImageLayouts));
        } catch (error) {
            console.error('Error removing layout from storage:', error);
        }
    }
}

// Check if there are any layouts and show placeholder if needed
function checkAndShowPlaceholder() {
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    const placeholder = document.querySelector('.garden-beds-placeholder');
    
    if (!gardenBedsContainer || !placeholder) return;
    
    const layoutElements = gardenBedsContainer.querySelectorAll('.garden-layout-snapshot, .garden-image-layout');
    
    if (layoutElements.length === 0) {
        placeholder.style.display = 'block';
    }
}

// Function to clear all garden layouts
function clearAllLayouts() {
    // Remove all layout elements from the DOM
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (gardenBedsContainer) {
        // Get all layout elements
        const layoutElements = gardenBedsContainer.querySelectorAll('.garden-image-layout');
        
        // Remove each element
        layoutElements.forEach(element => {
            element.remove();
        });
        
        // Clear localStorage
        localStorage.removeItem('garden_image_layouts');
        
        // Show the placeholder
        const placeholder = document.querySelector('.garden-beds-placeholder');
        if (placeholder) {
            placeholder.style.display = 'block';
        }
    }
}

// Function to connect the existing "Clear All Layouts" button
function connectClearAllButton() {
    const clearAllButton = document.getElementById('clear-all-layouts');
    if (clearAllButton) {
        clearAllButton.addEventListener('click', () => {
            showGardenMessageModal(
                'Warning',
                'Are you sure you want to clear all garden layouts and elements? This action cannot be undone.',
                {
                    confirmText: 'Yes',
                    cancelText: 'No',
                    onConfirm: () => clearAllLayouts()
                }
            );
        });
    }
}

// Add capture button and connect to the download entire plan button
function setupEventListeners() {
    // Connect the capture button event listener
    const captureButton = document.getElementById('capture-garden-image');
    if (captureButton) {
        captureButton.addEventListener('click', captureGardenLayoutAsImage);
    }
    
    // Connect the download entire plan button if it exists in HTML
    const downloadPlanButton = document.getElementById('download-garden-plan');
    if (downloadPlanButton) {
        downloadPlanButton.addEventListener('click', downloadEntireGardenPlan);
    }
    
    // Connect the clear all button
    connectClearAllButton();
}

// Main initialization function
export function initializeGardenLayout() {
    // Setup event listeners
    setupEventListeners();
    
    // Load any previously saved image layouts
    loadSavedImageLayouts();
    
    console.log('✅ Garden layout image functionality initialized');
}

/**
 * Capture the garden grid element as a PNG Blob (for Save to Journal).
 * Requires html2canvas to be loaded on window.
 * @returns {Promise<Blob>}
 */
export async function captureGardenGridAsBlob() {
    await loadExportLibraries();
    const gridElement = document.getElementById('grid');
    if (!gridElement) return Promise.reject(new Error('Garden grid not found'));
    if (typeof window.html2canvas === 'undefined') return Promise.reject(new Error('html2canvas is not loaded'));
    return window.html2canvas(gridElement, {
        backgroundColor: null,
        scale: 2,
        logging: false,
        allowTaint: true,
        useCORS: true
    }).then(canvas => {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Failed to create image blob'))), 'image/webp');
        });
    });
}

/**
 * Capture the Garden Plant Information summary table as a PNG Blob (for Save to Journal).
 * Requires html2canvas to be loaded on window.
 * @returns {Promise<Blob|null>} Resolves with blob, or null if summary container is missing/empty.
 */
export async function capturePlantSummaryAsBlob() {
    await loadExportLibraries();
    const summaryEl = document.getElementById('summary-container');
    if (!summaryEl || typeof window.html2canvas === 'undefined') return Promise.resolve(null);
    return window.html2canvas(summaryEl, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        allowTaint: true,
        useCORS: true
    }).then(canvas => {
        return new Promise((resolve) => {
            canvas.toBlob(blob => resolve(blob || null), 'image/webp');
        });
    }).catch(() => null);
}

/**
 * Capture the garden calendar section as a PNG Blob (for Save to Journal).
 * Clones #garden-calendar, removes the download button from the clone, then captures.
 * @returns {Promise<Blob|null>} Resolves with blob, or null if calendar section is missing.
 */
export async function captureGardenCalendarAsBlob() {
    await loadExportLibraries();
    const gardenCalendarSection = document.getElementById('garden-calendar');
    if (!gardenCalendarSection || typeof window.html2canvas === 'undefined') return Promise.resolve(null);
    const captureContainer = document.createElement('div');
    captureContainer.style.padding = '40px';
    captureContainer.style.backgroundColor = '#ffffff';
    const sectionClone = gardenCalendarSection.cloneNode(true);
    const downloadBtnClone = sectionClone.querySelector('.download-btn');
    if (downloadBtnClone) downloadBtnClone.remove();
    captureContainer.appendChild(sectionClone);
    document.body.appendChild(captureContainer);
    try {
        const canvas = await window.html2canvas(captureContainer, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            allowTaint: true,
            useCORS: true
        });
        return new Promise((resolve) => {
            canvas.toBlob(blob => resolve(blob || null), 'image/webp');
        });
    } finally {
        document.body.removeChild(captureContainer);
    }
}

/**
 * Capture the garden plan overview (garden-beds-container) as a PNG Blob (same as Download Garden Plan).
 * For use with Save to Journal from the Garden Plan section.
 * @returns {Promise<Blob>}
 */
export async function captureGardenPlanAsBlob() {
    await loadExportLibraries();
    const gardenBedsContainer = document.getElementById('garden-beds-container');
    if (!gardenBedsContainer) return Promise.reject(new Error('Garden plan container not found'));
    if (typeof window.html2canvas === 'undefined') return Promise.reject(new Error('html2canvas is not loaded'));
    return window.html2canvas(gardenBedsContainer, {
        backgroundColor: '#d5f4b9',
        scale: 2,
        logging: false,
        allowTaint: true,
        useCORS: true
    }).then(canvas => {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Failed to create image blob'))), 'image/webp');
        });
    });
}

// Export additional functions that might be needed by other modules
export {
    captureGardenLayoutAsImage,
    downloadEntireGardenPlan,
    clearAllLayouts,
    loadSavedImageLayouts
};

// Function to duplicate a layout
function duplicateLayout(element) {
    const layoutId = element.getAttribute('data-layout-id');
    const storedLayouts = localStorage.getItem('garden_image_layouts');
    
    if (storedLayouts) {
        try {
            const layouts = JSON.parse(storedLayouts);
            const originalLayout = layouts.find(layout => layout.id === layoutId);
            
            if (originalLayout) {
                // Create a new layout object with a new ID
                const newLayout = {
                    ...originalLayout,
                    id: Date.now().toString(),
                    name: originalLayout.name || '', // Keep original name or empty string if no name
                    position: {
                        left: originalLayout.position?.left + 20 || 20,
                        top: originalLayout.position?.top + 20 || 20
                    }
                };
                
                // Add the new layout to storage
                layouts.push(newLayout);
                localStorage.setItem('garden_image_layouts', JSON.stringify(layouts));
                
                // Add the new layout to the container
                addImageLayoutToGardenBedsContainer(newLayout);
            }
        } catch (error) {
            console.error('Error duplicating layout:', error);
        }
    }
} 